import { ReactNode, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  View,
  Platform,
  useWindowDimensions,
  Animated,
  Pressable,
  ImageBackground,
  ScrollView,
  FlatList,
  NativeScrollEvent,
  NativeSyntheticEvent,
  StyleSheet,
  StyleProp,
  ViewStyle,
} from 'react-native';
import { useRouter, Redirect } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { Body, Caption, ThemedText } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import { TripFlowLogo } from '@/components/graphics/TripFlowLogo';
import { VALUE_PROPOSITION_FEATURES, HERO_IMAGES } from '@/data/valuePropositionFeatures';
import { useUserStore } from '@/state/userStore';
import { createValuePropStyles } from '@/styles/valueProposition';

const AUTO_ROTATE_MS = 3000;
type ValuePropFeature = (typeof VALUE_PROPOSITION_FEATURES)[0];

type SharedStyles = ReturnType<typeof createValuePropStyles>;

function ValueCard({
  height,
  children,
  style,
}: {
  height?: number;
  children: ReactNode;
  style?: StyleProp<ViewStyle>;
}) {
  const theme = useTheme();
  const isWeb = Platform.OS === 'web';

  const baseStyle = useMemo(
    () => ({
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      borderRadius: theme.radii.xl,
      padding: theme.spacing(1.5),
      borderWidth: 1.5,
      borderColor: 'rgba(255, 255, 255, 0.28)',
      gap: theme.spacing(0.75),
      overflow: 'hidden' as const,
      ...theme.shadows.level3,
    }),
    [theme]
  );

  return (
    <View
      style={[
        baseStyle,
        height
          ? isWeb
            ? // Web text wraps differently; allow the card to grow instead of clipping content.
              ({ minHeight: height } as ViewStyle)
            : ({ height } as ViewStyle)
          : null,
        style,
      ]}
    >
      {children}
    </View>
  );
}

function FeaturePreview({
  feature,
  height,
  styles,
}: {
  feature: ValuePropFeature;
  height: number;
  styles: SharedStyles;
}) {
  const theme = useTheme();

  if (feature.id === 'itineraries') {
    return (
      <ValueCard height={height} style={{ justifyContent: 'center' }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), marginBottom: theme.spacing(0.25) }}>
          <View style={styles.iconContainer}>
            <Ionicons name="calendar-outline" size={22} color="#FFFFFF" />
          </View>
          <ThemedText variant="bodyStrong" style={{ fontSize: 14, color: '#FFFFFF', fontWeight: '700' }}>
            Itineraries
          </ThemedText>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
          <View style={styles.iconContainerSm}>
            <Ionicons name="calendar-outline" size={18} color="#FFFFFF" />
          </View>
          <View style={{ flex: 1 }}>
            <ThemedText variant="bodyStrong" style={{ fontSize: 13, color: '#FFFFFF' }}>
              Day 1 · Arrival
            </ThemedText>
            <Body style={{ fontSize: 12, color: 'rgba(255,255,255,0.9)' }}>09:00 Check-in · 12:30 Lunch</Body>
          </View>
        </View>
        <View style={{ height: 1, backgroundColor: 'rgba(255,255,255,0.2)', marginVertical: theme.spacing(0.5) }} />
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
          <View style={styles.iconContainerSm}>
            <Ionicons name="map-outline" size={18} color="#FFFFFF" />
          </View>
          <View style={{ flex: 1 }}>
            <ThemedText variant="bodyStrong" style={{ fontSize: 13, color: '#FFFFFF' }}>
              Afternoon Highlights
            </ThemedText>
            <Body style={{ fontSize: 12, color: 'rgba(255,255,255,0.9)' }}>Museum · Park · Dinner booking</Body>
          </View>
        </View>
      </ValueCard>
    );
  }

  if (feature.id === 'collaborate') {
    return (
      <ValueCard height={height}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), marginBottom: theme.spacing(0.25) }}>
          <View style={styles.iconContainer}>
            <Ionicons name="people-outline" size={22} color="#FFFFFF" />
          </View>
          <ThemedText variant="bodyStrong" style={{ fontSize: 14, color: '#FFFFFF', fontWeight: '700' }}>
            Collaboration
          </ThemedText>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5), marginBottom: theme.spacing(0.5) }}>
          <View style={styles.iconContainerSm}>
            <ThemedText style={{ color: '#FFFFFF', fontSize: 11, fontWeight: '700' }}>JD</ThemedText>
          </View>
          <View style={[styles.iconContainerSm, { marginLeft: -10 }]}>
            <ThemedText style={{ color: '#FFFFFF', fontSize: 11, fontWeight: '700' }}>SM</ThemedText>
          </View>
          <View style={[styles.iconContainerSm, { marginLeft: -10, backgroundColor: 'rgba(19,42,19,0.4)' }]}>
            <Ionicons name="add-outline" size={14} color="#FFFFFF" />
          </View>
          <Body style={{ color: '#FFFFFF', fontWeight: '600' }}>3 collaborators</Body>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
          <View style={styles.iconContainerSm}>
            <Ionicons name="chatbubble-outline" size={16} color="#FFFFFF" />
          </View>
          <View style={{ flex: 1 }}>
            <Body style={{ fontSize: 12, color: '#FFFFFF', fontWeight: '600' }}>Sarah: “Flight confirmed!”</Body>
            <Caption style={{ color: 'rgba(255,255,255,0.85)' }}>2 minutes ago</Caption>
          </View>
        </View>
      </ValueCard>
    );
  }

  if (feature.id === 'budget') {
    return (
      <ValueCard height={height}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75), marginBottom: theme.spacing(0.25) }}>
          <View style={[styles.iconContainer, { width: 36, height: 36 }]}>
            <Ionicons name="wallet-outline" size={20} color="#FFFFFF" />
          </View>
          <ThemedText variant="bodyStrong" style={{ fontSize: 13, color: '#FFFFFF', fontWeight: '700' }}>
            Budget Tracker
          </ThemedText>
        </View>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: theme.spacing(0.25) }}>
          <Body style={{ fontSize: 11, color: '#FFFFFF', fontWeight: '600' }}>Total Budget</Body>
          <ThemedText variant="bodyStrong" style={{ fontSize: 14, color: '#FFFFFF', fontWeight: '800' }}>
            $2,450 / $3,000
          </ThemedText>
        </View>
        <View
          style={{
            height: 8,
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            borderRadius: 4,
            overflow: 'hidden',
            marginBottom: theme.spacing(0.5),
          }}
        >
          <LinearGradient colors={['#132a13', '#132a13']} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }} style={{ height: '100%', width: '82%', borderRadius: 4 }} />
        </View>
        <View style={{ gap: theme.spacing(0.5), flex: 1 }}>
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingVertical: theme.spacing(0.5),
              paddingHorizontal: theme.spacing(0.75),
              backgroundColor: 'rgba(19, 42, 19, 0.3)',
              borderRadius: theme.radii.md,
              borderWidth: 1,
              borderColor: 'rgba(255, 255, 255, 0.15)',
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
              <Ionicons name="airplane-outline" size={14} color="#FFFFFF" />
              <Body style={{ fontSize: 11, color: '#FFFFFF', fontWeight: '600' }}>Flights</Body>
            </View>
            <ThemedText variant="bodyStrong" style={{ fontSize: 12, color: '#FFFFFF', fontWeight: '700' }}>
              $850
            </ThemedText>
          </View>
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingVertical: theme.spacing(0.5),
              paddingHorizontal: theme.spacing(0.75),
              backgroundColor: 'rgba(19, 42, 19, 0.3)',
              borderRadius: theme.radii.md,
              borderWidth: 1,
              borderColor: 'rgba(255, 255, 255, 0.15)',
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
              <Ionicons name="bed-outline" size={14} color="#FFFFFF" />
              <Body style={{ fontSize: 11, color: '#FFFFFF', fontWeight: '600' }}>Hotels</Body>
            </View>
            <ThemedText variant="bodyStrong" style={{ fontSize: 12, color: '#FFFFFF', fontWeight: '700' }}>
              $1,200
            </ThemedText>
          </View>
        </View>
      </ValueCard>
    );
  }

  if (feature.id === 'map') {
    return (
      <ValueCard height={height}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5), marginBottom: theme.spacing(0.25) }}>
          <View style={[styles.iconContainer, { width: 32, height: 32 }]}>
            <Ionicons name="map-outline" size={18} color="#FFFFFF" />
          </View>
          <ThemedText variant="bodyStrong" style={{ fontSize: 12, color: '#FFFFFF', fontWeight: '700' }}>
            Trip Map
          </ThemedText>
        </View>
        <View
          style={{
            flex: 1,
            borderRadius: theme.radii.lg,
            overflow: 'hidden',
            position: 'relative',
            backgroundColor: 'rgba(19, 42, 19, 0.3)',
            borderWidth: 1,
            borderColor: 'rgba(255, 255, 255, 0.2)',
            marginBottom: theme.spacing(0.25),
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Ionicons name="map-outline" size={28} color="#FFFFFF" />
          <View
            style={{
              position: 'absolute',
              top: 12,
              left: 20,
              width: 5,
              height: 5,
              borderRadius: 2.5,
              backgroundColor: '#FFFFFF',
              borderWidth: 1,
              borderColor: '#132a13',
            }}
          />
          <View
            style={{
              position: 'absolute',
              bottom: 16,
              right: 28,
              width: 5,
              height: 5,
              borderRadius: 2.5,
              backgroundColor: '#FFFFFF',
              borderWidth: 1,
              borderColor: '#132a13',
            }}
          />
        </View>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: theme.spacing(0.5),
            paddingVertical: theme.spacing(0.25),
            paddingHorizontal: theme.spacing(0.5),
            backgroundColor: 'rgba(19, 42, 19, 0.3)',
            borderRadius: theme.radii.md,
            borderWidth: 1,
            borderColor: 'rgba(255, 255, 255, 0.15)',
          }}
        >
          <View style={{ width: 20, height: 20, borderRadius: 10, backgroundColor: 'rgba(19, 42, 19, 0.5)', justifyContent: 'center', alignItems: 'center', borderWidth: 1, borderColor: 'rgba(255, 255, 255, 0.2)' }}>
            <Ionicons name="location-outline" size={12} color="#FFFFFF" />
          </View>
          <Body style={{ fontSize: 11, color: '#FFFFFF', fontWeight: '700' }}>5 places nearby</Body>
        </View>
      </ValueCard>
    );
  }

  if (feature.id === 'ai-companion') {
    return (
      <ValueCard height={height} style={{ padding: theme.spacing(1.25), gap: theme.spacing(0.75) }}>
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing(0.75) }}>
          <View style={[styles.iconContainer, { width: 36, height: 36, flexShrink: 0 }]}>
            <Ionicons name="sparkles-outline" size={20} color="#FFFFFF" />
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <ThemedText variant="bodyStrong" style={{ fontSize: 13, fontWeight: '800', color: '#FFFFFF' }}>
              Family trip companion
            </ThemedText>
            <Body style={{ fontSize: 11, color: 'rgba(255, 255, 255, 0.9)', marginTop: 2, fontWeight: '600' }}>
              Keeps your plan in sync and suggests quick fixes when you need them
            </Body>
          </View>
        </View>

        <View
          style={{
            backgroundColor: 'rgba(19, 42, 19, 0.35)',
            borderRadius: theme.radii.lg,
            borderWidth: 1,
            borderColor: 'rgba(255, 255, 255, 0.2)',
            padding: theme.spacing(1),
            gap: theme.spacing(0.5),
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing(0.5) }}>
            <Ionicons name="chatbubble-ellipses-outline" size={16} color="#FFFFFF" style={{ marginTop: 2, flexShrink: 0 }} />
            <Body style={{ fontSize: 11.5, color: '#FFFFFF', fontWeight: '600', flex: 1, minWidth: 0 }}>Tailored plans in seconds</Body>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing(0.5) }}>
            <Ionicons name="sparkles-outline" size={16} color="#FFFFFF" style={{ marginTop: 2, flexShrink: 0 }} />
            <Body style={{ fontSize: 11.5, color: '#FFFFFF', fontWeight: '600', flex: 1, minWidth: 0 }}>Smart re-plans when plans change</Body>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing(0.5) }}>
            <Ionicons name="notifications-outline" size={16} color="#FFFFFF" style={{ marginTop: 2, flexShrink: 0 }} />
            <Body style={{ fontSize: 11.5, color: '#FFFFFF', fontWeight: '600', flex: 1, minWidth: 0 }}>Helpful nudges and checklists for travel days</Body>
          </View>
        </View>

        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5), paddingVertical: theme.spacing(0.25) }}>
          <Ionicons name="trending-up-outline" size={16} color="#FFFFFF" style={{ flexShrink: 0 }} />
          <Caption style={{ color: 'rgba(255,255,255,0.9)', fontSize: 11, flex: 1, minWidth: 0 }}>Built to support families on the move</Caption>
        </View>
      </ValueCard>
    );
  }

  return (
    <ValueCard height={height} style={{ width: '100%', justifyContent: 'center', alignItems: 'center' }}>
      <View style={styles.iconContainer}>
        <Ionicons name={feature.icon} size={28} color="#FFFFFF" />
      </View>
    </ValueCard>
  );
}

export default function ValuePropositionScreen() {
  const router = useRouter();
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const { width, height } = useWindowDimensions();
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);
  const authReady = useUserStore((state) => state.authReady);
  const isAuthenticated = useUserStore((state) => state.isAuthenticated);
  const hasCompletedOnboarding = useUserStore((state) => state.hasCompletedOnboarding);
  const featureListRef = useRef<FlatList<ValuePropFeature>>(null);
  const autoRotateRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const buttonScale = useRef(new Animated.Value(1)).current;
  const isWeb = Platform.OS === 'web';
  const shouldRedirect = authReady && isAuthenticated && hasCompletedOnboarding;
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sharedStyles = useMemo(() => createValuePropStyles(theme), [theme]);
  const styles = useMemo(
    () =>
      StyleSheet.create({
        screen: { flex: 1 },
        background: {
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100%',
        },
        gradient: { flex: 1 },
        content: {
          ...(Platform.OS === 'web'
            ? // On web (especially embedded webviews), avoid flex:1 which can cause children
              // (like FlatList) to be height-constrained and clip their contents.
              { flexGrow: 1 }
            : { flex: 1 }),
          paddingTop:
            Platform.OS === 'web'
              ? Math.max(insets.top + theme.spacing(4), theme.spacing(6))
              : Math.max(insets.top + theme.spacing(3), theme.spacing(5)),
          paddingHorizontal: Platform.OS === 'web' ? theme.spacing(4) : theme.spacing(3),
          paddingBottom: insets.bottom + theme.spacing(2),
          zIndex: 1,
          alignItems: 'center',
          width: '100%',
        },
        skipButton: {
          position: 'absolute',
          top: Math.max(insets.top, theme.spacing(2)),
          right: theme.spacing(2),
          zIndex: 100,
          paddingVertical: theme.spacing(1.5),
          paddingHorizontal: theme.spacing(2.5),
          minHeight: 44,
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: theme.radii.pill,
          borderWidth: 1,
          borderColor: 'rgba(255, 255, 255, 0.3)',
        },
        header: {
          alignItems: 'center',
          // More breathing room between brand lockup and hero headline.
          gap: theme.spacing(2),
          marginBottom: Platform.OS === 'web' ? theme.spacing(2) : theme.spacing(1),
          maxWidth: Platform.OS === 'web' ? 700 : undefined,
        },
        title: {
          ...theme.typography.h1,
          fontSize: height < 700 ? 28 : Platform.OS === 'web' ? 40 : 34,
          fontWeight: '800',
          textAlign: 'center',
          color: '#FFFFFF',
          letterSpacing: -0.5,
          textShadowColor: 'rgba(0, 0, 0, 0.45)',
          textShadowOffset: { width: 0, height: 2 },
          textShadowRadius: 8,
          lineHeight: height < 700 ? 32 : Platform.OS === 'web' ? 46 : 40,
        },
        subtitle: {
          ...theme.typography.body,
          textAlign: 'center',
          fontSize: width < 360 ? 13 : height < 700 ? 14 : 16,
          maxWidth: Platform.OS === 'web' ? 520 : 420,
          color: 'rgba(255, 255, 255, 0.95)',
          lineHeight: width < 360 ? 18 : height < 700 ? 20 : 22,
        },
        featureContainer: {
          ...(Platform.OS === 'web'
            ? // Prevent flex-shrink on web so the list doesn't get clipped in shorter viewports.
              { flexGrow: 0, flexShrink: 0 }
            : { flex: 1 }),
          width: '100%',
          gap: theme.spacing(1),
          paddingBottom: theme.spacing(1),
          justifyContent: 'flex-start',
          alignItems: 'center',
          marginTop: Platform.OS === 'web' ? theme.spacing(0.5) : theme.spacing(1),
          paddingTop: Platform.OS === 'web' ? theme.spacing(0.5) : theme.spacing(1),
        },
        featureCardWrapper: {
          alignSelf: 'center',
          alignItems: 'center',
          gap: theme.spacing(1.25),
        },
        featureTextBlock: {
          alignItems: 'center',
          gap: theme.spacing(0.75),
        },
        featureDescription: {
          ...theme.typography.body,
          fontSize: height < 700 ? 15 : 17,
          textAlign: 'center',
          color: '#FFFFFF',
          fontWeight: '500',
          textShadowColor: 'rgba(0, 0, 0, 0.6)',
          textShadowOffset: { width: 0, height: 1 },
          textShadowRadius: 4,
          lineHeight: height < 700 ? 20 : 22,
        },
        indicatorsRow: {
          ...sharedStyles.indicatorsRow,
          marginBottom: theme.spacing(0.5),
        },
        ctaContainer: {
          marginTop: theme.spacing(1.25),
        },
        ctaButton: {
          backgroundColor: '#132a13',
          borderRadius: theme.radii.xl,
          paddingVertical: theme.spacing(3),
          paddingHorizontal: theme.spacing(5),
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: theme.spacing(2),
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 8 },
          shadowOpacity: 0.3,
          shadowRadius: 16,
          elevation: 12,
        },
      }),
    [height, insets.bottom, insets.top, sharedStyles.indicatorsRow, theme, width]
  );

  const handleContinue = () => {
    if (isAuthenticated) {
      if (hasCompletedOnboarding) {
        router.replace('/(tabs)');
      } else {
        router.replace('/(onboarding)/profile-details');
      }
    } else {
      router.replace('/(onboarding)/sign-in');
    }
  };

  const handleGetStarted = handleContinue;
  const handleSkip = handleContinue;

  const logoSize = height < 700 ? 100 : height < 900 ? 120 : 140;
  const isCompact = height < 700;
  const isTablet = !isWeb && width >= 768;
  const featureCardHeight = isCompact ? 180 : isTablet ? 210 : 200;
  // Give the AI card extra room so the last bullet can wrap without clipping (especially on smaller iPhones).
  const aiCompanionExtraHeight = isCompact ? 28 : 52;
  const maxFeatureCardHeight = featureCardHeight + aiCompanionExtraHeight;
  const featureSnapInterval = width; // snap to full viewport to ensure one card per page
  const featureCardWidth = Math.min(width - theme.spacing(6), isWeb ? 540 : 360);
  const featureCardContentWidth = Math.min(featureCardWidth - theme.spacing(4), isWeb ? 480 : 340);
  const activeFeatures: ValuePropFeature[] =
    VALUE_PROPOSITION_FEATURES.length > 0
      ? VALUE_PROPOSITION_FEATURES
      : [
          {
            id: 'core-planning-fallback',
            title: 'Plan and organize every trip',
            description: 'Tripvio keeps documents, itinerary, budget, and safety details in one place.',
            icon: 'compass-outline',
          } as ValuePropFeature,
        ];
  const usingFallbackFeature = VALUE_PROPOSITION_FEATURES.length === 0;
  // Reduce feature page height on mobile to prevent cutoff
  const featurePageHeight = isWeb 
    ? Math.min(maxFeatureCardHeight + theme.spacing(10), 520) 
    : Math.min(maxFeatureCardHeight + theme.spacing(8), height * 0.48);
  const featureListTopPadding = isWeb ? theme.spacing(1) : theme.spacing(1);

  const stopAutoRotate = useCallback(() => {
    if (autoRotateRef.current) {
      clearInterval(autoRotateRef.current);
      autoRotateRef.current = null;
    }
  }, []);

  const startAutoRotate = useCallback(() => {
    stopAutoRotate();
    if (activeFeatures.length <= 1) return;
    // Only start auto-rotate if user is not manually scrolling
    autoRotateRef.current = setInterval(() => {
      if (!isScrollingRef.current) {
        setCurrentFeatureIndex((prevIndex) => {
          const nextIndex = (prevIndex + 1) % activeFeatures.length;
          featureListRef.current?.scrollToIndex({ index: nextIndex, animated: true });
          return nextIndex;
        });
      }
    }, AUTO_ROTATE_MS);
  }, [activeFeatures.length, stopAutoRotate]);

  useEffect(() => {
    if (shouldRedirect) {
      return;
    }
    // Center initial card once layout is ready
    const timeoutId = setTimeout(() => {
      featureListRef.current?.scrollToIndex({
        index: currentFeatureIndex,
        animated: false,
      });
    }, 100);

    startAutoRotate();
    return () => {
      clearTimeout(timeoutId);
      stopAutoRotate();
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [currentFeatureIndex, shouldRedirect, startAutoRotate, stopAutoRotate]);

  const handleButtonPressIn = () => {
    Animated.spring(buttonScale, {
      toValue: 0.97,
      tension: 300,
      friction: 20,
      useNativeDriver: true,
    }).start();
  };

  const handleButtonPressOut = () => {
    Animated.spring(buttonScale, {
      toValue: 1,
      tension: 300,
      friction: 20,
      useNativeDriver: true,
    }).start();
  };

  const handleScrollEnd = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const offsetX = event.nativeEvent.contentOffset.x;
    const index = Math.round(offsetX / featureSnapInterval);
    const clampedIndex = Math.max(0, Math.min(index, activeFeatures.length - 1));
    
    if (clampedIndex !== currentFeatureIndex) {
      setCurrentFeatureIndex(clampedIndex);
    }
    
    // Mark scrolling as complete after a short delay to prevent auto-rotate from interfering
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingRef.current = false;
      startAutoRotate();
    }, 300);
  };

  const handleIndicatorPress = (index: number) => {
    isScrollingRef.current = true;
    setCurrentFeatureIndex(index);
    featureListRef.current?.scrollToIndex({ index, animated: true });
    // Restart auto-rotate after scroll completes
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingRef.current = false;
      startAutoRotate();
    }, 500);
  };

  const staticBackgroundImage = HERO_IMAGES[0];

  if (shouldRedirect) {
    return <Redirect href="/(tabs)" />;
  }

  return (
    <View style={styles.screen}>
      <ImageBackground
        source={staticBackgroundImage}
        defaultSource={require('../../assets/travel/travel-1.jpg')}
        style={styles.background}
        resizeMode="cover"
      >
        <LinearGradient
          colors={['rgba(0,0,0,0.25)', 'rgba(0,0,0,0.5)', 'rgba(0,0,0,0.65)']}
          locations={[0, 0.5, 1]}
          style={styles.gradient}
        />
      </ImageBackground>

      {isWeb ? (
        // Cursor's embedded browser / smaller viewports can reduce available height.
        // Make the screen scrollable on web so feature cards never get clipped.
        <ScrollView
          style={{ flex: 1, width: '100%' }}
          contentContainerStyle={[styles.content, { flexGrow: 1 }]}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.header} accessibilityLiveRegion="polite">
            <TripFlowLogo size={logoSize} wordmarkVariant="eyebrow" />
            <ThemedText variant="h1" style={styles.title}>
              Everything your family needs, in one place
            </ThemedText>
            <Body style={styles.subtitle}>Docs, schedule, budget, safety—ready even offline.</Body>
            {usingFallbackFeature ? (
              <Caption color="rgba(255,255,255,0.9)" style={{ textAlign: 'center' }}>
                Feature highlights are loading. You can still continue.
              </Caption>
            ) : null}
          </View>

          <View style={styles.featureContainer}>
            <FlatList
              ref={featureListRef}
              data={activeFeatures}
              horizontal
              pagingEnabled={!isWeb}
              snapToInterval={featureSnapInterval}
              decelerationRate="fast"
              snapToAlignment="start"
              showsHorizontalScrollIndicator={false}
              keyExtractor={(item) => item.id}
              style={{
                width: featureSnapInterval,
                alignSelf: 'center',
              }}
              scrollEnabled
              contentContainerStyle={{
                paddingVertical: featureListTopPadding,
              }}
              getItemLayout={(_, index) => ({
                length: featureSnapInterval,
                offset: featureSnapInterval * index,
                index,
              })}
              onScrollBeginDrag={() => {
                isScrollingRef.current = true;
                stopAutoRotate();
              }}
              onMomentumScrollEnd={handleScrollEnd}
              onScrollEndDrag={(event) => {
                if (Platform.OS !== 'web') {
                  handleScrollEnd(event);
                } else {
                  // For web, also handle scroll end
                  isScrollingRef.current = false;
                  if (scrollTimeoutRef.current) {
                    clearTimeout(scrollTimeoutRef.current);
                  }
                  scrollTimeoutRef.current = setTimeout(() => {
                    startAutoRotate();
                  }, 300);
                }
              }}
              bounces={Platform.OS === 'ios'}
              renderItem={({ item }) => (
                <View
                  style={{
                    width: featureSnapInterval,
                    ...(isWeb ? { minHeight: featurePageHeight } : { height: featurePageHeight }),
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingHorizontal: theme.spacing(2),
                  }}
                >
                  <View style={[styles.featureCardWrapper, { width: featureCardWidth, maxWidth: '100%' }]}>
                    <View style={[styles.featureTextBlock, { width: featureCardContentWidth, maxWidth: '100%' }]}>
                      <ThemedText variant="title" style={sharedStyles.featureTitle}>
                        {item.title}
                      </ThemedText>
                      <Body style={[styles.featureDescription, { maxWidth: featureCardContentWidth }]}>
                        {item.description}
                      </Body>
                    </View>
                    <View
                      style={{
                        width: featureCardContentWidth,
                        maxWidth: '100%',
                        ...(isWeb
                          ? { minHeight: item.id === 'ai-companion' ? maxFeatureCardHeight : featureCardHeight }
                          : { height: item.id === 'ai-companion' ? maxFeatureCardHeight : featureCardHeight }),
                      }}
                    >
                      <FeaturePreview
                        feature={item}
                        height={item.id === 'ai-companion' ? maxFeatureCardHeight : featureCardHeight}
                        styles={sharedStyles}
                      />
                    </View>
                  </View>
                </View>
              )}
            />

            <View style={styles.indicatorsRow}>
              {activeFeatures.map((item, index) => (
                <Pressable
                  key={item.id}
                  onPress={() => handleIndicatorPress(index)}
                  style={sharedStyles.indicatorTapArea}
                  accessibilityRole={Platform.OS === 'android' ? 'button' : 'tab'}
                  accessibilityLabel={`Show ${item.title}`}
                  accessibilityState={{ selected: index === currentFeatureIndex }}
                >
                  <View
                    style={[
                      sharedStyles.indicatorDot,
                      index === currentFeatureIndex && sharedStyles.indicatorDotActive,
                    ]}
                  />
                </Pressable>
              ))}
            </View>
          </View>

          <Animated.View style={[styles.ctaContainer, { transform: [{ scale: buttonScale }] }]}>
            <Pressable
              onPress={handleGetStarted}
              onPressIn={handleButtonPressIn}
              onPressOut={handleButtonPressOut}
              style={({ pressed }) => [styles.ctaButton, { opacity: pressed ? 0.9 : 1 }]}
              accessibilityRole="button"
              accessibilityLabel="Get started with Tripvio"
            >
              <ThemedText
                variant="bodyStrong"
                style={{
                  fontSize: 18,
                  fontWeight: '700',
                  color: '#FFFFFF',
                }}
              >
                Get Started
              </ThemedText>
              <Ionicons name="arrow-forward" size={22} color="#FFFFFF" />
            </Pressable>
          </Animated.View>
        </ScrollView>
      ) : (
        <View style={styles.content}>
        <View style={styles.header} accessibilityLiveRegion="polite">
          <TripFlowLogo size={logoSize} wordmarkVariant="eyebrow" />
          <ThemedText variant="h1" style={styles.title}>
            Everything your family needs, in one place
          </ThemedText>
          <Body style={styles.subtitle}>Docs, schedule, budget, safety—ready even offline.</Body>
          {usingFallbackFeature ? (
            <Caption color="rgba(255,255,255,0.9)" style={{ textAlign: 'center' }}>
              Feature highlights are loading. You can still continue.
            </Caption>
          ) : null}
        </View>

        <View style={styles.featureContainer}>
          <FlatList
            ref={featureListRef}
            data={activeFeatures}
            horizontal
            pagingEnabled={!isWeb}
            snapToInterval={featureSnapInterval}
            decelerationRate="fast"
            snapToAlignment="start"
            showsHorizontalScrollIndicator={false}
            keyExtractor={(item) => item.id}
            style={{ 
              width: featureSnapInterval,
              alignSelf: 'center',
            }}
            scrollEnabled
            contentContainerStyle={{
              paddingVertical: featureListTopPadding,
            }}
            getItemLayout={(_, index) => ({
              length: featureSnapInterval,
              offset: featureSnapInterval * index,
              index,
            })}
            onScrollBeginDrag={() => {
              isScrollingRef.current = true;
              stopAutoRotate();
            }}
            onMomentumScrollEnd={handleScrollEnd}
            onScrollEndDrag={(event) => {
              if (Platform.OS !== 'web') {
                handleScrollEnd(event);
              } else {
                // For web, also handle scroll end
                isScrollingRef.current = false;
                if (scrollTimeoutRef.current) {
                  clearTimeout(scrollTimeoutRef.current);
                }
                scrollTimeoutRef.current = setTimeout(() => {
                  startAutoRotate();
                }, 300);
              }
            }}
            bounces={Platform.OS === 'ios'}
            renderItem={({ item }) => (
              // AI companion card is denser; give it a bit more vertical room so bullets can wrap without clipping.
              // Keep other cards at the base height for visual consistency.
              // (FeaturePreview uses the passed height to size its internal ValueCard.)
              <View
                style={{
                  width: featureSnapInterval,
                  ...(isWeb ? { minHeight: featurePageHeight } : { height: featurePageHeight }),
                  alignItems: 'center',
                  justifyContent: 'center',
                  paddingHorizontal: theme.spacing(2),
                }}
              >
                <View style={[styles.featureCardWrapper, { width: featureCardWidth, maxWidth: '100%' }]}>
                  <View style={[styles.featureTextBlock, { width: featureCardContentWidth, maxWidth: '100%' }]}>
                    <ThemedText variant="title" style={sharedStyles.featureTitle}>
                      {item.title}
                    </ThemedText>
                    <Body style={[styles.featureDescription, { maxWidth: featureCardContentWidth }]}>
                      {item.description}
                    </Body>
                  </View>
                  <View
                    style={{
                      width: featureCardContentWidth,
                      maxWidth: '100%',
                      ...(isWeb
                        ? { minHeight: item.id === 'ai-companion' ? maxFeatureCardHeight : featureCardHeight }
                        : { height: item.id === 'ai-companion' ? maxFeatureCardHeight : featureCardHeight }),
                    }}
                  >
                    <FeaturePreview
                      feature={item}
                      height={item.id === 'ai-companion' ? maxFeatureCardHeight : featureCardHeight}
                      styles={sharedStyles}
                    />
                  </View>
                </View>
              </View>
            )}
          />

          <View style={styles.indicatorsRow}>
            {activeFeatures.map((item, index) => (
              <Pressable
                key={item.id}
                onPress={() => handleIndicatorPress(index)}
                style={sharedStyles.indicatorTapArea}
                accessibilityRole={Platform.OS === 'android' ? 'button' : 'tab'}
                accessibilityLabel={`Show ${item.title}`}
                accessibilityState={{ selected: index === currentFeatureIndex }}
              >
                <View
                  style={[
                    sharedStyles.indicatorDot,
                    index === currentFeatureIndex && sharedStyles.indicatorDotActive,
                  ]}
                />
              </Pressable>
            ))}
          </View>
        </View>

        <Animated.View style={[styles.ctaContainer, { transform: [{ scale: buttonScale }] }]}>
          <Pressable
            onPress={handleGetStarted}
            onPressIn={handleButtonPressIn}
            onPressOut={handleButtonPressOut}
            style={({ pressed }) => [styles.ctaButton, { opacity: pressed ? 0.9 : 1 }]}
            accessibilityRole="button"
            accessibilityLabel="Get started with Tripvio"
          >
            <ThemedText
              variant="bodyStrong"
              style={{
                fontSize: 18,
                fontWeight: '700',
                color: '#FFFFFF',
              }}
            >
              Get Started
            </ThemedText>
            <Ionicons name="arrow-forward" size={22} color="#FFFFFF" />
          </Pressable>
        </Animated.View>
        </View>
      )}
    </View>
  );
}
